// Reusable Schema.org JSON-LD generators for SEO

import { SITE_URL, SITE_NAME } from "@/components/SEO";
import { CONTACT } from "@/lib/data";

const dayMap = {
  "Lun., Ven.": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  Samedi: ["Saturday"],
  Dimanche: ["Sunday"],
};

const buildOpeningHours = () =>
  CONTACT.hours
    .filter((h) => h.slots !== "Fermé")
    .flatMap((h) => {
      const days = dayMap[h.day] || [];
      // Parse slots like "10h–12h · 14h–17h" or "10h–12h"
      const slots = h.slots.split("·").map((s) => s.trim());
      return slots.flatMap((slot) => {
        const m = slot.match(/(\d{1,2})h(?:(\d{2}))?[–-](\d{1,2})h(?:(\d{2}))?/);
        if (!m) return [];
        const [, h1, m1 = "00", h2, m2 = "00"] = m;
        return days.map((d) => ({
          "@type": "OpeningHoursSpecification",
          dayOfWeek: `https://schema.org/${d}`,
          opens: `${h1.padStart(2, "0")}:${m1}`,
          closes: `${h2.padStart(2, "0")}:${m2}`,
        }));
      });
    });

export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: "Terre et Mer Conciergerie",
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.svg`,
  description:
    "Conciergerie Airbnb premium en Bretagne et sur la Côte d'Émeraude. Pilotage complet de votre location courte durée par une équipe locale 24h/24 et 7j/7, avec permanence de nuit pour les voyageurs.",
  email: CONTACT.email,
  telephone: CONTACT.phone,
  areaServed: [
    { "@type": "AdministrativeArea", name: "Ille-et-Vilaine" },
    { "@type": "AdministrativeArea", name: "Côtes-d'Armor" },
    { "@type": "Place", name: "Côte d'Émeraude" },
    { "@type": "Place", name: "Bretagne" },
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "40 Bis Rue de Dinard",
    postalCode: "35730",
    addressLocality: "Pleurtuit",
    addressRegion: "Bretagne",
    addressCountry: "FR",
  },
};

export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "ProfessionalService"],
  "@id": `${SITE_URL}/#localbusiness`,
  name: SITE_NAME,
  image: `${SITE_URL}/favicon.svg`,
  url: SITE_URL,
  telephone: CONTACT.phone,
  email: CONTACT.email,
  priceRange: "€€",
  description:
    "Conciergerie Airbnb premium pour propriétaires de location courte durée en Bretagne (Côte d'Émeraude & Morbihan) : pilotage 24h/24 · 7j/7, accueil voyageurs avec permanence de nuit, ménage hôtelier, photos professionnelles, optimisation des revenus.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "40 Bis Rue de Dinard",
    postalCode: "35730",
    addressLocality: "Pleurtuit",
    addressRegion: "Bretagne",
    addressCountry: "FR",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 48.5743,
    longitude: -2.0576,
  },
  areaServed: [
    "Saint-Malo",
    "Cancale",
    "Dinard",
    "Dinan",
    "Saint-Cast-le-Guildo",
    "Saint-Briac-sur-Mer",
    "Saint-Lunaire",
    "Lancieux",
    "Pleurtuit",
    "Pléboulle",
    "Mont-Dol",
    "Damgan",
    "La Roche-Bernard",
    "Ambon",
    "Marzan",
    "Péaule",
    "Côte d'Émeraude",
    "Morbihan",
    "Bretagne",
  ],
  openingHoursSpecification: buildOpeningHours(),
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    bestRating: "5",
    ratingCount: "127",
  },
  sameAs: [],
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  description:
    "Conciergerie Airbnb premium en Bretagne, Saint-Malo, Dinard, Cancale, Dinan, Côte d'Émeraude & Morbihan (Damgan, La Roche-Bernard).",
  inLanguage: "fr-FR",
  publisher: { "@id": `${SITE_URL}/#organization` },
};

export const buildServiceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Conciergerie Airbnb",
  provider: { "@id": `${SITE_URL}/#localbusiness` },
  name: "Services de conciergerie Airbnb clé en main",
  description:
    "Multidiffusion, accueil voyageurs 24h/24 · 7j/7 (permanence de nuit), ménage hôtelier, linge, photos professionnelles, optimisation tarifaire et maintenance pour la location courte durée en Bretagne (Côte d'Émeraude & Morbihan).",
  areaServed: {
    "@type": "Place",
    name: "Bretagne, Côte d'Émeraude & Morbihan",
  },
  offers: {
    "@type": "Offer",
    price: "15",
    priceCurrency: "EUR",
    description: "Commission unique de 15% TTC, sans frais cachés.",
  },
});

export const buildFAQSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});

export const buildCitySchema = (city) => {
  const baseAreaServed = {
    "@type": "City",
    name: city.name,
    containedInPlace: {
      "@type": "AdministrativeArea",
      name: city.region,
    },
  };

  // Add geo coordinates when available (boosts local SEO)
  if (city.geo) {
    baseAreaServed.geo = {
      "@type": "GeoCoordinates",
      latitude: city.geo.lat,
      longitude: city.geo.lng,
    };
    if (city.postalCode) {
      baseAreaServed.address = {
        "@type": "PostalAddress",
        addressLocality: city.name,
        postalCode: city.postalCode,
        addressRegion: city.region,
        addressCountry: "FR",
      };
    }
  }

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Conciergerie Airbnb",
    name: `Conciergerie Airbnb à ${city.name}`,
    description: `Service de conciergerie Airbnb à ${city.name} (${city.region}). Pilotage complet de votre location courte durée par une équipe locale, intervention en moins de 15 minutes, commission unique 15 % TTC sans frais cachés.`,
    provider: { "@id": `${SITE_URL}/#localbusiness` },
    areaServed: baseAreaServed,
    url: `${SITE_URL}/villes/${city.slug}`,
    offers: {
      "@type": "Offer",
      priceSpecification: {
        "@type": "PriceSpecification",
        price: "15",
        priceCurrency: "EUR",
        valueAddedTaxIncluded: true,
        description: "Commission unique de 15% TTC sur les revenus, sans frais cachés.",
      },
      availability: "https://schema.org/InStock",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "127",
      bestRating: "5",
      worstRating: "1",
    },
  };
};
